import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Spinner } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { uploadVenueImages,deleteVenueImage } from "../../../../api";
import ConfirmationDialog from "../../../../components/common/ConfirmationDialog";
interface Props {
  venueData: any;
  venueId: number | null;
  refreshVenue: () => Promise<void>;
}

const VenueImagesTab = ({ venueData, venueId, refreshVenue }: Props) => {
  const [venueImages, setVenueImages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [newImages, setNewImages] = useState<any[]>([]);
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "warning" as "warning" | "danger" | "info" | "success",
  });
  const closeConfirmationDialog = () => {
    setConfirmationDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const showConfirmationDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    type: "warning" | "danger" | "info" | "success" = "warning"
  ) => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type,
    });
  };
  const handleDeleteExistingImage = (id: string) => {
    showConfirmationDialog(
      "Delete Image",
      "Are you sure you want to delete this image? This action cannot be undone.",
      async () => {
        if (!venueId) return;

        try {
          await deleteVenueImage(venueId, id);
          toast.success("Image deleted successfully!");
          await refreshVenue();
          closeConfirmationDialog();
        } catch {
          toast.error("Delete failed");
          closeConfirmationDialog();
        }
      },
      "danger"
    );
  };



  const onDrop = useCallback((acceptedFiles: File[]) => {
    const files = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      description: "",
      isExisting: false,
    }));

    setNewImages((prev) => [...prev, ...files]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    multiple: true,
  });
  const updateDescription = (index: number, value: string) => {
    const updated = [...newImages];
    updated[index].description = value;
    setNewImages(updated);
  };
  const removeNewImage = (index: number) => {
    const updated = [...newImages];
    URL.revokeObjectURL(updated[index].preview);
    updated.splice(index, 1);
    setNewImages(updated);
  };
  const removeExistingImage = async (id: string) => {
    if (!venueId) return;

    try {
      await deleteVenueImage(venueId, id);
      toast.success("Image deleted");
      refreshVenue();
    } catch {
      toast.error("Delete failed");
    }
  };

  const finishEvent = async () => {
    if (!venueId) return;
    setIsLoading(true);
    setError(null);
    let currentIdx = 0;
    try {
      for (let i = 0; i < newImages.length; i++) {
        currentIdx = i;
        setUploadingIndex(i);
        const img = newImages[i];
        const formData = new FormData();
        formData.append("description", img.description || "");
        formData.append("file", img.file);

        await uploadVenueImages(venueId, formData);
      }
      toast.success("Images uploaded successfully!");
      setNewImages([]);
      await refreshVenue();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Upload failed");
      setNewImages((prev) => prev.slice(currentIdx));
    } finally {
      setIsLoading(false);
      setUploadingIndex(null);
    }
  };
 useEffect(() => {
    if (!venueData) return;
    const existing = venueData?.venueImageFiles ?? [];
    setExistingImages(
      existing.map((img: any) => ({
        id: img.id,
        preview: img.file,
        description: img.description || "",
        isExisting: true,
      }))
    );
  }, [venueData]);
  return (
    <div className="tab-from-content">
      <ToastContainer autoClose={2000} />
      <div className="venue-tab-card main-card">
        <h4 className="venue-tab-title">
          <i className="fa-solid fa-image" />
          Add Venue Images
        </h4>
        <div className="venue-tab-body">
          <p className="text-muted small mb-3">
            Recommended: upload images in <strong>3:2</strong> ratio or landscape for best display.
          </p>
          <div {...getRootProps()} className="venue-dropzone mb-4">
            <input {...getInputProps()} />
            <p>Drag & drop images here, or click to select</p>
          </div>

          <div className="row g-2">
            {newImages.length > 0 &&
              newImages.map((img: any, index: number) => {
                const isUploading = isLoading && index === uploadingIndex;
                const isUploaded = isLoading && uploadingIndex !== null && index < uploadingIndex;

                return (
                  <div className="col-lg-4 col-md-4 col-sm-6" key={index}>
                    <div className="venue-image-card position-relative">
                      <div className="venue-image-wrap">
                        <img src={img.preview} alt="Venue" />
                      </div>
                      {isUploading && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white bg-dark bg-opacity-75" style={{ zIndex: 2 }}>
                          <Spinner size="sm" color="light" />
                          <span className="small mt-1 text-center font-weight-bold">Uploading...</span>
                        </div>
                      )}
                      {isUploaded && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white bg-dark bg-opacity-50" style={{ zIndex: 2 }}>
                          <i className="fa-solid fa-circle-check text-success fa-lg mb-1" />
                          <span className="small text-center">Success</span>
                        </div>
                      )}
                      <button
                        type="button"
                        className="venue-image-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNewImage(index);
                        }}
                        aria-label="Remove image"
                        disabled={isLoading}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                      <div className="venue-image-caption">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Caption (optional)"
                          value={img.description || ""}
                          onChange={(e) =>
                            updateDescription(index, e.target.value)
                          }
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              {isLoading && (
                <div className="col-12 mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="small text-muted font-weight-medium">Uploading images...</span>
                    <span className="small font-weight-bold" style={{ color: "#ee3f7c" }}>
                      {newImages.length > 0 && uploadingIndex !== null ? `${Math.round((uploadingIndex / newImages.length) * 100)}%` : "0%"}
                    </span>
                  </div>
                  <div className="progress" style={{ height: "6px", backgroundColor: "#f5f5f5", borderRadius: "3px", overflow: "hidden" }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{
                        width: `${newImages.length > 0 && uploadingIndex !== null ? (uploadingIndex / newImages.length) * 100 : 0}%`,
                        backgroundColor: "#ee3f7c",
                        transition: "width 0.3s ease-in-out",
                      }}
                    />
                  </div>
                </div>
              )}
              {!isLoading && newImages.length > 0 && (
              <div className="col-12 d-flex justify-content-end mt-3">
                <button
                  className="btn btn-default btn-hover steps_btn"
                  onClick={finishEvent}
                >
                  Save
                </button>
              </div>
              )}
              {error && (
                <div className="alert alert-danger mt-3 mb-0" role="alert">
                  {error}
                </div>
              )}
              <hr/>
               {existingImages.length > 0 && (
              <>
                <h5 className="my-1">Previously Uploaded Images</h5>
                <div className="row g-2">
                  {existingImages.map((img, index) => (
                    <div className="col-lg-4 col-md-4 col-sm-6" key={img.id}>
                      <div className="venue-image-card position-relative">
                        <div className="venue-image-wrap">
                          <img src={img.preview} alt="Existing Venue" />
                        </div>

                        <button
                          type="button"
                          className="venue-image-delete"
                          onClick={() => handleDeleteExistingImage(img.id)}
                        >
                          <i className="fa-solid fa-trash" />
                        </button>

                        <div className="venue-image-caption">
                          <input
                            type="text"
                            className="form-control"
                            value={img.description}
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        toggle={closeConfirmationDialog}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        type={confirmationDialog.type}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        icon="fa-solid fa-exclamation-triangle"
      />
    </div>
  );
};

export default VenueImagesTab;
